import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Tooltip } from '@mantine/core';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Alert, Card, CardBody, Col, Row } from 'reactstrap';

import PdfPreviewModal from '../../../../Components/General/Modal/PdfPreviewModal';
import { ODataGrid } from '../../../../Components/OElements/ODataGrid';
import { downloadFile } from '../../../../slices/thunks';
import { getResidueMovementsFeed } from '../../../../slices/residueIntake/thunk';
import { restartResidueIntake } from '../../../../slices/residueIntake/reducer';
import { useRespelEstablishmentToolbarGate } from '../hooks/useRespelEstablishmentToolbarGate';
import {
	RESPEL_NO_WAREHOUSE_ALERT_COPY,
	RESPEL_NO_WAREHOUSE_DETAIL_COPY,
} from '../respelNoWarehouseCopy';
import AddEditIntakeModal from './Add_Edit/AddEditIntakeModal';
import DeleteIntakeModal from './Delete/DeleteIntakeModal';
import {
	formatRespelIntakeDueDateLabel,
	getRespelIntakeDaysRemainingUtc,
	getRespelIntakeExpiryBadgeClass,
} from '../utils/respelIntakeLegalExpiry';

const isMongoObjectIdString = value =>
	typeof value === 'string' && /^[a-fA-F0-9]{24}$/.test(value);

/**
 * Listado timeline RESPEL: ingresos directos, recepciones por guía y salidas
 * ``WAREHOUSE_SEND_PARTIAL`` (D-003, D-013). Solo filas DIRECT admiten editar/eliminar.
 */
const IntakeList = ({ establishmentId }) => {
	const dispatch = useDispatch();
	const establishmentReady = useMemo(
		() => isMongoObjectIdString(establishmentId),
		[establishmentId],
	);

	const { isAdded, isUpdated, isDeleted } = useSelector(state => ({
		isAdded: state.ResidueIntakes.isAdded,
		isUpdated: state.ResidueIntakes.isUpdated,
		isDeleted: state.ResidueIntakes.isDeleted,
	}));

	const [feedReady, setFeedReady] = useState(false);
	const [inboundTotalCount, setInboundTotalCount] = useState(undefined);
	const [refetchRemoteData, setRefetchRemoteData] = useState(false);
	const [pdfPreview, setPdfPreview] = useState({
		open: false,
		blob: null,
		title: '',
	});
	const [pdfLoadingDocId, setPdfLoadingDocId] = useState(null);

	useEffect(() => {
		setFeedReady(false);
		setInboundTotalCount(undefined);
	}, [establishmentId]);

	const feedLoading = establishmentReady && !feedReady;

	const {
		showNoWarehouseAlert,
		warehouseVerifyError,
		warehouseCheckLoading,
		createEnabled,
	} = useRespelEstablishmentToolbarGate({
		establishmentId,
		establishmentReady,
		list: [],
		isLoadingList: feedLoading,
		listError: null,
		feedItemCount: inboundTotalCount,
	});

	useEffect(() => {
		if (!establishmentReady) return;
		if (!isAdded && !isUpdated && !isDeleted) return;
		setRefetchRemoteData(v => !v);
		dispatch(restartResidueIntake());
	}, [
		dispatch,
		establishmentReady,
		isAdded,
		isUpdated,
		isDeleted,
	]);

	const getDataFunc = useCallback(
		query => async innerDispatch => {
			const page = query?.pagination?.page ?? 1;
			const pageSize = query?.pagination?.pageSize ?? 50;
			try {
				const res = await innerDispatch(
					getResidueMovementsFeed({
						establishment_id: establishmentId,
						page,
						page_size: pageSize,
					}),
				).unwrap();
				setInboundTotalCount(res?.meta?.totalCount ?? 0);
				return {
					data: Array.isArray(res?.data) ? res.data : [],
					meta: { totalCount: res?.meta?.totalCount ?? 0 },
				};
			} catch (err) {
				setInboundTotalCount(0);
				throw err;
			} finally {
				setFeedReady(true);
			}
		},
		[establishmentId],
	);

	const [dataResidueIntake, setDataResidueIntake] = useState({});
	const [isEdit, setIsEdit] = useState(false);
	const [modal, setModal] = useState(false);
	const [modalDelete, setModalDelete] = useState(false);
	const [activeTab, setActiveTab] = useState('1');

	const toggleTab = useCallback(tab => {
		setActiveTab(prev => (prev !== tab ? tab : prev));
	}, []);

	const handleToggleModal = () => {
		setModal(prev => !prev);
		toggleTab('1');
	};
	const handleToggleModalDelete = () => setModalDelete(prev => !prev);

	const handleClickDelete = useCallback(intake => {
		setDataResidueIntake(intake);
		setModalDelete(true);
	}, []);
	const handleClickEdit = useCallback(intake => {
		setDataResidueIntake(intake);
		setIsEdit(true);
		setModal(true);
		setActiveTab('1');
	}, []);

	const closePdfPreview = useCallback(() => {
		setPdfPreview({ open: false, blob: null, title: '' });
	}, []);

	const handleClickPdf = useCallback(
		async row => {
			const documentId = row.document_id;
			const folio = row.folio;
			if (!documentId) {
				return;
			}
			try {
				setPdfLoadingDocId(documentId);
				const result = await dispatch(
					downloadFile({ file_id: documentId, disable_msg: true }),
				);
				const blob = result.payload;
				if (!(blob instanceof Blob)) {
					throw new Error('No se pudo obtener el archivo');
				}
				setPdfPreview({
					open: true,
					blob,
					title: `Guía de Despacho #${folio ?? ''}`,
				});
			} catch (e) {
				console.error('Error al obtener el PDF:', e);
				toast.error(`Error al cargar el PDF de la guía #${folio ?? ''}`);
			} finally {
				setPdfLoadingDocId(null);
			}
		},
		[dispatch],
	);

	const displayStatusForRow = row => {
		const isDirect = row.source === 'DIRECT' || !row.source;
		if (isDirect && row.availability_status) {
			return row.availability_status;
		}
		if (isDirect && row.status === 'AVA') {
			return 'ACTIVE';
		}
		return row.status;
	};

	const renderStatusBadge = status => {
		const statusMap = {
			ACTIVE: { label: 'Activo', color: 'primary' },
			AVA: { label: 'Activo', color: 'primary' },
			WITH_STOCK: { label: 'Con stock', color: 'success' },
			CONSUMED: { label: 'Consumido', color: 'warning' },
			CANCELLED: { label: 'Cancelado', color: 'secondary' },
			RECEIVED: { label: 'Recepción guía', color: 'info' },
			SENT: { label: 'Despacho stock', color: 'warning' },
		};
		const cfg = statusMap[status] || {
			label: status || '-',
			color: 'light text-dark',
		};
		return <span className={`badge bg-${cfg.color}`}>{cfg.label}</span>;
	};

	const columns = useMemo(
		() => [
			{
				header: 'Acciones',
				enableSorting: false,
				enableColumnFilter: false,
				enableGlobalFilter: false,
				enableColumnActions: false,
				accessorKey: 'residueIntake',
				columnDefType: 'display',
				size: 'auto',
				Cell: cellProps => {
					const row = cellProps.row.original;
					const isDirect = row.source === 'DIRECT' || !row.source;
					const isOutbound =
						row.direction === 'OUT' || row.source === 'GUIDE_SEND_STOCK';
					const docId = row.document_id;
					const pdfDisabled = !docId;
					const pdfLoading =
						!!docId && pdfLoadingDocId && docId === pdfLoadingDocId;

					const availability = displayStatusForRow(row);
					const remKg = Number(row.kilograms_remaining);
					const origKg = Number(row.kilograms);
					const lotNotIntact =
						isDirect &&
						Number.isFinite(remKg) &&
						Number.isFinite(origKg) &&
						remKg < origKg - 1e-6;
					const editDisabled =
						!isDirect ||
						isOutbound ||
						availability === 'CANCELLED' ||
						lotNotIntact;
					let editTip = 'Editar ingreso directo';
					if (!isDirect || isOutbound) {
						editTip =
							'Solo los ingresos directos pueden editarse. Recepciones y despachos no se editan desde aquí.';
					} else if (availability === 'CANCELLED') {
						editTip = 'No se puede editar un ingreso cancelado.';
					} else if (lotNotIntact) {
						editTip =
							'No se puede editar: parte del stock de este ingreso ya fue consumido.';
					}

					const deleteDisabled =
						!isDirect ||
						isOutbound ||
						availability === 'CANCELLED' ||
						availability === 'CONSUMED';
					let deleteTip = 'Eliminar ingreso directo';
					if (!isDirect || isOutbound) {
						deleteTip =
							'Solo los ingresos directos pueden eliminarse. Recepciones y despachos no se eliminan desde aquí.';
					} else if (availability === 'CANCELLED') {
						deleteTip = 'No se puede eliminar un ingreso cancelado desde aquí.';
					} else if (availability === 'CONSUMED') {
						deleteTip =
							'No queda stock por cancelar en este ingreso (ya consumido).';
					}

					const pdfTip = pdfDisabled
						? 'Sin documento PDF asociado a esta fila.'
						: 'Ver documento (PDF)';

					return (
						<div className='d-flex align-items-center gap-2 flex-nowrap'>
							<Tooltip label={pdfTip} position='top' withinPortal>
								<span className='d-inline-block'>
									<Link
										to='#'
										className={pdfDisabled ? 'pe-none opacity-50' : ''}
										onClick={e => {
											e.preventDefault();
											if (!pdfDisabled) handleClickPdf(row);
										}}
										aria-disabled={pdfDisabled}
									>
										{pdfLoading ? (
											<span
												className='spinner-border spinner-border-sm text-primary'
												role='status'
											/>
										) : (
											<i className='ri-file-pdf-line align-bottom text-danger' />
										)}
									</Link>
								</span>
							</Tooltip>
							<Tooltip label={editTip} position='top' withinPortal>
								<span className='d-inline-block'>
									<Link
										to='#'
										className={editDisabled ? 'pe-none opacity-50' : ''}
										onClick={e => {
											e.preventDefault();
											if (!editDisabled) handleClickEdit(row);
										}}
										aria-disabled={editDisabled}
									>
										<i className='ri-pencil-fill align-bottom text-muted' />
									</Link>
								</span>
							</Tooltip>
							<Tooltip label={deleteTip} position='top' withinPortal>
								<span className='d-inline-block'>
									<Link
										to='#'
										className={
											deleteDisabled
												? 'remove-item-btn pe-none opacity-50'
												: 'remove-item-btn'
										}
										onClick={e => {
											e.preventDefault();
											if (!deleteDisabled) handleClickDelete(row);
										}}
										aria-disabled={deleteDisabled}
									>
										<i className='ri-delete-bin-line align-bottom text-muted' />
									</Link>
								</span>
							</Tooltip>
						</div>
					);
				},
			},
			{
				header: 'Dirección',
				accessorKey: 'direction',
				enableSorting: false,
				enableColumnFilter: false,
				size: 'auto',
				Cell: ({ row }) => {
					const d = row.original.direction;
					if (d === 'OUT') {
						return <span className='badge bg-danger'>Salida</span>;
					}
					return <span className='badge bg-success'>Entrada</span>;
				},
			},
			{
				header: 'Tipo',
				accessorKey: 'source',
				enableSorting: false,
				enableColumnFilter: false,
				size: 'auto',
				Cell: ({ row }) => {
					const src = row.original.source;
					if (src === 'GUIDE_RECEPTION') {
						return (
							<span className='badge bg-primary'>Recepción guía</span>
						);
					}
					if (src === 'GUIDE_SEND_STOCK') {
						return (
							<span className='badge bg-warning text-dark'>
								Despacho stock
							</span>
						);
					}
					return <span className='badge bg-secondary'>Ingreso Directo</span>;
				},
			},
			{
				header: 'Fecha',
				accessorKey: 'intake_date',
				dataType: 'date',
				size: 'auto',
			},
			{
				header: 'Vencimiento',
				accessorKey: 'legal_expiry_days',
				enableSorting: false,
				enableColumnFilter: false,
				size: 'auto',
				Cell: ({ row }) => {
					const r = row.original;
					if (r.status === 'CANCELLED') {
						return '—';
					}
					if (r.direction === 'OUT' || r.source === 'GUIDE_SEND_STOCK') {
						return '—';
					}
					const remaining = getRespelIntakeDaysRemainingUtc(r.intake_date);
					if (remaining === null) {
						return '—';
					}
					const cls = getRespelIntakeExpiryBadgeClass(remaining);
					const dueLabel = formatRespelIntakeDueDateLabel(r.intake_date);
					const title = dueLabel
						? `Último día del plazo (180 d, UTC): ${dueLabel}`
						: undefined;
					return (
						<span title={title} className={cls}>
							{remaining}
						</span>
					);
				},
			},
			{
				header: 'Bodega',
				accessorKey: 'warehouse.name',
				enableSorting: false,
				size: 'auto',
				Cell: ({ row }) =>
					row.original.warehouse?.name ||
					row.original.warehouse_name ||
					row.original.warehouse_id ||
					'-',
			},
			{
				header: 'Residuo',
				accessorKey: 'residue.name',
				enableSorting: false,
				size: 'auto',
				Cell: ({ row }) =>
					row.original.residue?.name ||
					row.original.residue_name ||
					row.original.residue_id ||
					'-',
			},
			{
				header: 'Kilogramos',
				accessorKey: 'kilograms',
				enableSorting: false,
				size: 'auto',
				Cell: ({ row }) => {
					const kg = Number(row.original.kilograms);
					return Number.isFinite(kg)
						? kg.toLocaleString('es-CL', { maximumFractionDigits: 2 })
						: '-';
				},
			},
			{
				header: 'Kg remanentes',
				accessorKey: 'kilograms_remaining',
				enableSorting: false,
				size: 'auto',
				Cell: ({ row }) => {
					const r = row.original;
					const isDirect = r.source === 'DIRECT' || !r.source;
					if (!isDirect) {
						return '—';
					}
					const rem = Number(r.kilograms_remaining);
					if (!Number.isFinite(rem)) {
						return '—';
					}
					return rem.toLocaleString('es-CL', { maximumFractionDigits: 2 });
				},
			},
			{
				header: 'Estado',
				accessorKey: 'status',
				enableSorting: false,
				size: 'auto',
				visible: false,
				Cell: ({ row }) => renderStatusBadge(displayStatusForRow(row.original)),
			},
			{
				header: 'Descripción',
				accessorKey: 'origin_description',
				size: 'auto',
				visible: false,
			},
			{
				header: 'Especie',
				accessorKey: 'especie',
				size: 'auto',
				visible: false,
			},
			{
				header: '¿Tiene consumo?',
				accessorKey: 'has_consumption',
				size: 'auto',
				visible: false,
				Cell: ({ row }) => (row.original.has_consumption ? 'Sí' : 'No'),
			},
		],
		[handleClickDelete, handleClickEdit, handleClickPdf, pdfLoadingDocId],
	);

	const createTooltip = useMemo(() => {
		if (!establishmentReady) return '';
		if (feedLoading) return 'Esperando la carga del listado…';
		if (warehouseCheckLoading) return 'Verificando bodegas RESPEL…';
		if (warehouseVerifyError) return warehouseVerifyError;
		if (showNoWarehouseAlert) return RESPEL_NO_WAREHOUSE_DETAIL_COPY;
		if (!createEnabled) return 'Espere a completar la carga.';
		return 'Registrar nuevo ingreso directo';
	}, [
		establishmentReady,
		feedLoading,
		warehouseCheckLoading,
		warehouseVerifyError,
		showNoWarehouseAlert,
		createEnabled,
	]);

	const intakeToolbar = useMemo(() => {
		if (!establishmentReady) return undefined;
		return () => (
			<div className='d-flex align-items-center justify-content-between gap-2 w-100 min-w-0'>
				<div className='d-flex justify-content-start gap-2 align-items-center flex-shrink-0'>
					<Tooltip label={createTooltip} position='top' withinPortal>
						<span className='d-inline-block'>
							<button
								type='button'
								className='btn d-flex align-items-center btn-tooltip-hover'
								disabled={!createEnabled}
								onClick={() => {
									setIsEdit(false);
									setDataResidueIntake({});
									setModal(true);
									toggleTab('1');
								}}
							>
								<i
									className={`${window.Icons?.plus || 'icon-plus'} label-icon icon-tooltop-size`}
								></i>
								{' '}
								Nuevo ingreso directo
							</button>
						</span>
					</Tooltip>
				</div>
				{showNoWarehouseAlert || warehouseVerifyError ? (
					<div className='min-w-0 flex-grow-1 ms-2'>
						{showNoWarehouseAlert ? (
							<div
								role='status'
								className='alert alert-warning small py-1 px-2 mb-0 text-truncate lh-sm'
							>
								{RESPEL_NO_WAREHOUSE_ALERT_COPY}
							</div>
						) : null}
						{warehouseVerifyError ? (
							<div
								role='alert'
								className='alert alert-danger small py-1 px-2 mb-0 text-truncate lh-sm'
							>
								{warehouseVerifyError}
							</div>
						) : null}
					</div>
				) : null}
			</div>
		);
	}, [
		establishmentReady,
		createEnabled,
		createTooltip,
		showNoWarehouseAlert,
		warehouseVerifyError,
		toggleTab,
	]);

	return (
		<React.Fragment>
			{!establishmentReady ? (
				<Alert color='warning' className='mb-3'>
					Establecimiento no válido o no seleccionado. Use el menú con un centro
					activo o el selector global.
				</Alert>
			) : null}
			<Row>
				<Col lg={12}>
					<Card>
						<CardBody>
							{establishmentReady ? (
								<ODataGrid
									key={establishmentId}
									columns={columns}
									getDataFunc={getDataFunc}
									getRowIdTable={row => row.row_id || String(row._id)}
									refetchRemoteData={refetchRemoteData}
									filtering={false}
									sorting={false}
									remoteOperations={{
										pagination: true,
										sorting: false,
										filtering: false,
									}}
									searching={false}
									nameFile={'movimientos_respel'}
									customToolbarActions={intakeToolbar}
								/>
							) : null}
						</CardBody>
					</Card>
				</Col>
			</Row>

			<PdfPreviewModal
				isOpen={pdfPreview.open}
				onClose={closePdfPreview}
				blob={pdfPreview.blob}
				title={pdfPreview.title}
			/>

			<AddEditIntakeModal
				dataResidueIntake={dataResidueIntake}
				isEdit={isEdit}
				isOpen={modal}
				handleToggleModal={handleToggleModal}
				activeTab={activeTab}
				toggleTab={toggleTab}
				establishmentId={establishmentId}
			/>
			<DeleteIntakeModal
				dataResidueIntake={dataResidueIntake}
				isOpen={modalDelete}
				handlerToggleModal={handleToggleModalDelete}
			/>
		</React.Fragment>
	);
};

export default IntakeList;
